import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  MoreVertical,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import DataTable from "../ui/DataTable";
import { templateService } from "../../services/templateService";
import { useAuth } from "../../context/AuthContext";
import ConfirmDialog from "../ui/ConfirmDialog";
import toast from "react-hot-toast";
import { getStoredClientId } from "../../utils/tokenUtils";

const TemplatesTab = ({ clientId: propClientId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("test_case"); // 'test_case' or 'test_run'

  // Use prop clientId if provided, otherwise get from user
  const effectiveClientId =
    propClientId || getStoredClientId() || user?.client_id;
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filter templates based on active tab
  const templates = allTemplates.filter((t) => {
    const layoutType = t.layout_type || "test_case"; // Default to test_case for backward compatibility
    return layoutType === activeTab;
  });

  useEffect(() => {
    if (effectiveClientId) {
      loadTemplates();
    }
  }, [effectiveClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.getTemplates({
        client_id: effectiveClientId,
      });
      if (response.success && response.data?.templates) {
        const templates = response.data.templates;
        setAllTemplates(templates);

        // If no templates exist, try to initialize default templates
        if (templates.length === 0) {
          try {
            await templateService.initializeDefaultTemplate();
            // Reload templates after initialization
            const retryResponse = await templateService.getTemplates({
              client_id: effectiveClientId,
            });
            if (retryResponse.success && retryResponse.data?.templates) {
              setAllTemplates(retryResponse.data.templates);
            }
          } catch (initError) {
            console.error("Error initializing default templates:", initError);
            // Don't show error toast - just log it
          }
        }
      } else {
        setAllTemplates([]);
        // Try to initialize if no templates found
        try {
          await templateService.initializeDefaultTemplate();
          const retryResponse = await templateService.getTemplates({
            client_id: effectiveClientId,
          });
          if (retryResponse.success && retryResponse.data?.templates) {
            setAllTemplates(retryResponse.data.templates);
          }
        } catch (initError) {
          console.error("Error initializing default templates:", initError);
        }
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load templates");
      setAllTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableDisable = async (template) => {
    if (!template) return;

    // Prevent disabling default layouts
    const isDefault =
      template?.is_default === true ||
      template?.type === "system" ||
      template?.name === "Default" ||
      template?.name === "Test Case Steps";
    if (isDefault && template.status === "enabled") {
      toast.error("Default layouts cannot be disabled");
      return;
    }

    try {
      const newStatus = template.status === "enabled" ? "disabled" : "enabled";
      await templateService.updateTemplate(template.template_id, {
        status: newStatus,
      });
      toast.success(
        `Layout ${newStatus === "enabled" ? "enabled" : "disabled"} successfully`,
      );
      loadTemplates();
    } catch (error) {
      console.error("Error updating template status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update layout status",
      );
    }
  };

  const handleClone = async (template) => {
    if (!template) return;

    try {
      await templateService.cloneTemplate(
        template.template_id,
        `${template.name} - Copy`,
      );
      toast.success("Template cloned successfully");
      loadTemplates();
    } catch (error) {
      console.error("Error cloning template:", error);
      toast.error("Failed to clone template");
    }
  };

  const handleDelete = (template) => {
    // Prevent deletion of default templates
    const isDefault =
      template?.is_default === true ||
      template?.type === "system" ||
      template?.name === "Default";
    if (isDefault) {
      toast.error("Default layouts cannot be deleted");
      return;
    }
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    // Double-check before deletion
    const isDefault =
      selectedTemplate?.is_default === true ||
      selectedTemplate?.type === "system" ||
      selectedTemplate?.name === "Default";
    if (isDefault) {
      toast.error("Default layouts cannot be deleted");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      return;
    }

    try {
      setDeleteLoading(true);
      await templateService.deleteTemplate(selectedTemplate.template_id);
      toast.success("Template deleted successfully");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data?.message || "Failed to delete template");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Layout Name",
      render: (value, row) => {
        // Show "Default" for default templates (including old "Test Case Steps" name)
        const isDefault =
          row?.is_default === true ||
          row?.type === "system" ||
          row?.name === "Default" ||
          row?.name === "Test Case Steps";
        const displayName = isDefault ? "Default" : value || "-";
        return <span className="font-medium">{displayName}</span>;
      },
    },
    {
      key: "layout_type",
      header: "Layout Type",
      render: (value) => {
        const layoutType = value || "test_case"; // Default to test_case for backward compatibility
        return (
          <Badge variant={"outline"}>
            {layoutType === "test_run" ? "Test Run" : "Test Case"}
          </Badge>
        );
      },
    },
    {
      key: "type",
      header: "Type",
      render: (value) => (
        <Badge variant={value === "system" ? "default" : "secondary"}>
          {value === "system" ? "System" : "Custom"}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (value) => (
        <Badge variant={value === "enabled" ? "default" : "secondary"}>
          {value === "enabled" ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      key: "is_default",
      header: "Default",
      render: (value) => (value ? <Badge variant="default">Yes</Badge> : "-"),
    },
    {
      key: "linked_projects_count",
      header: "Linked Projects",
      render: (value) => value || 0,
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => {
        const isDefault =
          row?.is_default === true ||
          row?.type === "system" ||
          row?.name === "Default" ||
          row?.name === "Test Case Steps";
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  if (isDefault) {
                    toast.error("Default layouts cannot be edited");
                  } else {
                    const clientId = effectiveClientId || user?.client_id;
                    if (clientId) {
                      navigate(
                        `/templates/create?template_id=${row.template_id}&client_id=${clientId}&layout_type=${row.layout_type || "test_case"}`,
                      );
                    } else {
                      toast.error("Please select a client first");
                    }
                  }
                }}
                disabled={isDefault}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Configure Layout
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleClone(row)}>
                <Copy className="w-4 h-4 mr-2" />
                Clone Layout
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleEnableDisable(row)}
                disabled={isDefault && row?.status === "enabled"}
              >
                {row?.status === "enabled" ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Disable Layout
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Enable Layout
                  </>
                )}
              </DropdownMenuItem>
              {!isDefault && row?.type === "custom" && (
                <DropdownMenuItem
                  onClick={() => handleDelete(row)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Layout
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Test Case / Test Run Layout</h2>
          <p className="text-gray-600 text-sm mt-1">
            Create and manage test case and test run layouts for your projects
          </p>
        </div>
        <Button
          onClick={() => {
            const clientId = effectiveClientId || user?.client_id;
            if (clientId) {
              navigate(
                `/templates/create?client_id=${clientId}&layout_type=${activeTab}`,
              );
            } else {
              toast("Please select a client first");
            }
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create {activeTab === "test_run" ? "Test Run" : "Test Case"} Layout
        </Button>
      </div>

      {/* Tabs for filtering by layout type */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("test_case")}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "test_case"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300"
            }`}
          >
            Test Case Layouts
          </button>
          <button
            onClick={() => setActiveTab("test_run")}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "test_run"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300"
            }`}
          >
            Test Run Layouts
          </button>
        </nav>
      </div>

      <div className="card overflow-hidden">
        <DataTable
          data={templates}
          columns={columns}
          loading={loading}
          emptyMessage={`No ${activeTab === "test_run" ? "test run" : "test case"} layouts found. Create your first layout to get started.`}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedTemplate(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Layout"
        message={`Are you sure you want to delete "${selectedTemplate?.name}"? This action cannot be undone.`}
        confirmText="Delete Layout"
        confirmVariant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default TemplatesTab;
